/**
 * Intake Resolution queue (Phase Keystone K5.2a; C4 made it PATH-AWARE) — the global, cross-import
 * hold-pile for BOTH clearance (supplier_sheet) AND standing (price_sheet) rows ingestion could NOT
 * confidently dispose: no clean GTIN/ASIN, or a clean key matching >=2 masters. The action set routes
 * off the held row's import sheet_mode (Decision E, via import_id -> ingestion_batches.sheet_mode):
 *   • Link-to-master  -> supplier_sheet: resolve_clearance_hold (child lot + alias) | price_sheet:
 *                        refresh_standing_cost (living cost in place, no lot).
 *   • Confirm-new      -> supplier_sheet: confirm_clearance_new (master + first lot) | price_sheet:
 *                        create_standing_product (flat master, no lot).
 *   • Dismiss          -> status='dismissed' (no product). All four paths write a decision_snapshot
 *                        (Decision I — what the human saw + the action; the match-tuning corpus).
 * (DD-36 DISCHARGED — U10 A3, 2026-07-15: this surface was renamed Clearance -> Intake — component
 * IntakeResolution, file, route /catalog/intake, nav 'Intake', routeManifest. docsRegistry unchanged:
 * /catalog/intake resolves via the '/catalog' prefix fallback (no per-route slug existed). Naming-only
 * scope (reg:1453/1460): the DB clearance RPCs, is_clearance_lot, and the lane-descriptive body copy
 * naming clearance/standing row types are domain vocabulary and are kept.)
 * dist_rep is excluded by the table RLS (reads 0 rows); writes are admin/user. The Link-to-master
 * modal surfaces ADVISORY fuzzy suggestions (K5.2b — suggestMasterCandidates over a brand-scoped,
 * CAPPED candidate pool); a suggestion pre-fills the human pick (the existing resolve_clearance_hold
 * path), never auto-resolves.
 *
 * A SIMPLE table (not AG Grid) by design: this is a transient action queue that operators drain
 * (resolved/dismissed rows leave the 'held' view), bounded by triage throughput — not a 100K-row
 * data grid. AG Grid migration is a K5.3 polish only if the held volume warrants virtualization.
 */
import { Fragment, useState, useEffect, useCallback, type JSX } from 'react';
import { RefreshCw, Link2, PlusCircle, XCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../hooks/useApp';
import { FormModal } from '../../components/ui/FormModal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { Toast, type ToastState } from '../../components/ui/Toast';
import { suggestMasterCandidates } from '../../lib/clearanceResolve';
import { normalizeBrand, type MatchVerdict } from '../../lib/matchScore';
import type { Json } from '../../lib/database.types';

interface HeldRow {
  id: string;
  import_id: string;
  source_row_number: number | null;
  upc: string | null;
  supplier_part_no: string | null;
  model: string | null;
  brand: string | null;
  title: string | null;
  cost_usd: number | null;
  raw_row: Record<string, unknown> | null;
  created_at: string;
  // C4 — the held row's path, derived via import_id -> ingestion_batches.sheet_mode (Decision E, the free
  // discriminator). Routes the action set: 'price_sheet' -> standing RPCs (no lot); 'supplier_sheet' ->
  // clearance RPCs (lot).
  sheet_mode: string;
}

// C4 (Decision I) — the write-once audit/training snapshot: what the human saw (advisory suggestions +
// scores + verdicts) + the action + the master chosen. Captured at the terminal transition; the dismiss
// carries a labeled-reject snapshot (the human saw the row + chose no master — a negative tuning example).
interface DecisionSnapshot {
  action: 'link' | 'confirm_new' | 'dismiss';
  master_chosen: string | null;
  suggestions: Array<{ master_id: string; score: number; verdict: MatchVerdict }>;
  picked_from_suggestions: boolean;
}

interface MasterOption {
  id: string;
  isg_sku: string;
  title: string | null;
  upc: string | null;
  brand: string | null;
}

// Candidate-pool cap for the fuzzy suggester — bounds per-open scoring (GUARDRAIL: never score one
// held row against hundreds). 100 covers the catalog's largest single-brand master count (Altronix,
// 63) with headroom; a brand beyond this is bounded, never the full catalog.
const CANDIDATE_POOL_CAP = 100;

// A ranked advisory suggestion the picker surfaces — MasterOption + the matchScore confidence.
interface SuggestionRow extends MasterOption {
  score: number;
  verdict: MatchVerdict;
}

// ── Master picker modal — search existing masters (parent_product_id IS NULL) and pick one ──
function MasterPickerModal({
  open,
  heldRow,
  onCancel,
  onPick,
}: {
  open: boolean;
  heldRow: HeldRow | null;
  onCancel: () => void;
  onPick: (masterId: string, snapshot: DecisionSnapshot) => void;
}): JSX.Element | null {
  const [q, setQ] = useState<string>('');
  const [results, setResults] = useState<MasterOption[]>([]);
  const [searching, setSearching] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<SuggestionRow[]>([]);
  const [suggesting, setSuggesting] = useState<boolean>(false);

  // On open, surface advisory fuzzy suggestions: a brand-scoped, CAPPED candidate pool (the GUARDRAIL
  // — never the full catalog) scored by suggestMasterCandidates. Brandless rows get no suggestions
  // (manual search only). NEVER auto-resolves — a suggestion just pre-fills the human pick (onPick).
  useEffect(() => {
    if (!open || !heldRow) {
      setSuggestions([]);
      return;
    }
    setQ('');
    setResults([]);
    setSuggestions([]);
    const brandFold = normalizeBrand(heldRow.brand);
    if (brandFold === null) return; // brandless -> no suggestions; search manually
    let cancelled = false;
    setSuggesting(true);
    void (async () => {
      const { data } = await supabase
        .from('products')
        .select('id, isg_sku, title, upc, brand, model, mpn, supplier_part_no')
        .is('parent_product_id', null)
        .ilike('brand', heldRow.brand ?? '')
        .order('isg_sku', { ascending: true })
        .limit(CANDIDATE_POOL_CAP);
      if (cancelled) return;
      const pool = (data ?? []).filter((m) => normalizeBrand(m.brand) === brandFold);
      const byId = new Map(pool.map((m) => [m.id, m] as const));
      const ranked = suggestMasterCandidates(
        {
          supplier_part_no: heldRow.supplier_part_no,
          model: heldRow.model,
          brand: heldRow.brand,
          title: heldRow.title,
        },
        pool.map((m) => ({
          id: m.id,
          supplier_part_no: m.supplier_part_no,
          mpn: m.mpn,
          model: m.model,
          brand: m.brand,
          title: m.title,
        }))
      );
      const display: SuggestionRow[] = [];
      for (const s of ranked) {
        if (s.verdict === 'hold') continue; // a hold-score isn't a suggestion — surface suspect+ only
        const m = byId.get(s.masterId);
        if (m)
          display.push({
            id: m.id,
            isg_sku: m.isg_sku,
            title: m.title,
            upc: m.upc,
            brand: m.brand,
            score: s.score,
            verdict: s.verdict,
          });
      }
      if (!cancelled) {
        setSuggestions(display);
        setSuggesting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, heldRow]);

  const search = useCallback(async (term: string): Promise<void> => {
    setQ(term);
    const trimmed = term.trim();
    if (trimmed.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    const like = `%${trimmed}%`;
    const { data } = await supabase
      .from('products')
      .select('id, isg_sku, title, upc, brand')
      .is('parent_product_id', null)
      .or(`isg_sku.ilike.${like},title.ilike.${like},upc.ilike.${like}`)
      .limit(20);
    setResults((data ?? []) as MasterOption[]);
    setSearching(false);
  }, []);

  // C4 (Decision I) — the decision_snapshot for a pick: the advisory suggestions shown + the chosen master.
  const linkSnapshot = (masterId: string): DecisionSnapshot => ({
    action: 'link',
    master_chosen: masterId,
    suggestions: suggestions.map((s) => ({
      master_id: s.id,
      score: Math.round(s.score),
      verdict: s.verdict,
    })),
    picked_from_suggestions: suggestions.some((s) => s.id === masterId),
  });

  if (!open || !heldRow) return null;

  const ident =
    heldRow.title ?? heldRow.supplier_part_no ?? heldRow.model ?? heldRow.upc ?? heldRow.id;

  return (
    <FormModal
      open={open}
      title="Link to master"
      subtitle={`Clearance row: ${ident}`}
      size="2xl"
      onClose={onCancel}
      footer={
        <button onClick={onCancel} className="arbi-btn-ghost text-xs px-3 py-1.5">
          Cancel
        </button>
      }
    >
      <div className="space-y-3">
        {suggesting ? (
          <p className="text-2xs text-ink-muted">Finding suggestions…</p>
        ) : suggestions.length > 0 ? (
          <div className="space-y-1">
            <p className="text-2xs font-medium uppercase tracking-wider text-ink-muted">
              Suggested matches
            </p>
            <div className="divide-y divide-border-subtle overflow-hidden rounded-md border border-border-subtle">
              {suggestions.map((m) => (
                <button
                  key={m.id}
                  onClick={() => onPick(m.id, linkSnapshot(m.id))}
                  className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs hover:bg-surface-2"
                >
                  <span className="font-mono text-ink-bright">{m.isg_sku}</span>
                  <span className="flex-1 truncate text-ink-secondary">{m.title ?? '—'}</span>
                  <span
                    className={`rounded px-1 py-0.5 text-2xs font-medium ${
                      m.verdict === 'trust'
                        ? 'bg-success/15 text-success'
                        : 'bg-warning/15 text-warning'
                    }`}
                  >
                    {Math.round(m.score)}
                  </span>
                </button>
              ))}
            </div>
            <p className="text-2xs text-ink-muted">
              Advisory — confirm a suggestion or search below.
            </p>
          </div>
        ) : null}
        <input
          type="text"
          value={q}
          onChange={(e) => void search(e.target.value)}
          aria-label="Search masters"
          placeholder="Search masters by SKU, title, or UPC…"
          className="arbi-input w-full px-2 py-1 text-xs"
        />
        <div className="max-h-72 divide-y divide-border-subtle overflow-y-auto rounded-md border border-border-subtle">
          {searching ? (
            <p className="py-4 text-center text-xs text-ink-muted">Searching…</p>
          ) : results.length === 0 ? (
            <p className="py-4 text-center text-xs text-ink-muted">
              {q.trim().length < 2 ? 'Type at least 2 characters to search.' : 'No masters match.'}
            </p>
          ) : (
            results.map((m) => (
              <button
                key={m.id}
                onClick={() => onPick(m.id, linkSnapshot(m.id))}
                className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs hover:bg-surface-2"
              >
                <span className="font-mono text-ink-bright">{m.isg_sku}</span>
                <span className="flex-1 truncate text-ink-secondary">{m.title ?? '—'}</span>
                <span className="font-mono text-2xs text-ink-muted">{m.upc ?? ''}</span>
              </button>
            ))
          )}
        </div>
      </div>
    </FormModal>
  );
}

export default function IntakeResolution(): JSX.Element {
  const { isViewer, isDistRep } = useApp();
  const canWrite = !isViewer && !isDistRep; // admin + user write; viewer read-only; dist_rep RLS-excluded

  const [rows, setRows] = useState<HeldRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [pickerFor, setPickerFor] = useState<HeldRow | null>(null);
  const [dismissFor, setDismissFor] = useState<HeldRow | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    const { data, error: e } = await supabase
      .from('intake_resolution_queue')
      .select(
        'id, import_id, source_row_number, upc, supplier_part_no, model, brand, title, cost_usd, raw_row, created_at, ingestion_batches(sheet_mode)'
      )
      .eq('status', 'held')
      .order('created_at', { ascending: true })
      .limit(200);
    if (e) setError(e.message);
    // C4 — flatten the import's sheet_mode off the embed (Rule-17 verified the join). The FK import_id is
    // NOT NULL so the embed always resolves; the fallback is defensive only.
    else
      setRows(
        (data ?? []).map((r) => ({
          ...r,
          sheet_mode:
            (r.ingestion_batches as { sheet_mode?: string } | null)?.sheet_mode ?? 'supplier_sheet',
        })) as unknown as HeldRow[]
      );
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onResolve = useCallback(
    async (row: HeldRow, masterId: string, snapshot: DecisionSnapshot): Promise<void> => {
      setBusyId(row.id);
      // C4 — path-aware: price_sheet -> refresh_standing_cost (living cost, no lot); supplier_sheet ->
      // resolve_clearance_hold (child lot + alias). Both take p_decision_snapshot.
      const standing = row.sheet_mode === 'price_sheet';
      const { error: e } = standing
        ? await supabase.rpc('refresh_standing_cost', {
            p_queue_id: row.id,
            p_master_id: masterId,
            p_decision_snapshot: snapshot as unknown as Json,
          })
        : await supabase.rpc('resolve_clearance_hold', {
            p_queue_id: row.id,
            p_master_id: masterId,
            p_decision_snapshot: snapshot as unknown as Json,
          });
      setToast(
        e
          ? { kind: 'error', message: `Link failed: ${e.message}` }
          : {
              kind: 'success',
              message: standing
                ? 'Linked — standing cost refreshed.'
                : 'Linked — child lot created + alias seeded.',
            }
      );
      setPickerFor(null);
      setBusyId(null);
      await load();
    },
    [load]
  );

  const onConfirmNew = useCallback(
    async (row: HeldRow): Promise<void> => {
      setBusyId(row.id);
      const snapshot: DecisionSnapshot = {
        action: 'confirm_new',
        master_chosen: null,
        suggestions: [],
        picked_from_suggestions: false,
      };
      // C4 — path-aware: price_sheet -> create_standing_product (flat master, no lot); supplier_sheet ->
      // confirm_clearance_new (master + first lot).
      const standing = row.sheet_mode === 'price_sheet';
      const { error: e } = standing
        ? await supabase.rpc('create_standing_product', {
            p_queue_id: row.id,
            p_decision_snapshot: snapshot as unknown as Json,
          })
        : await supabase.rpc('confirm_clearance_new', {
            p_queue_id: row.id,
            p_decision_snapshot: snapshot as unknown as Json,
          });
      setToast(
        e
          ? { kind: 'error', message: `Confirm-new failed: ${e.message}` }
          : {
              kind: 'success',
              message: standing
                ? 'New standing master created.'
                : 'New master + first lot created.',
            }
      );
      setBusyId(null);
      await load();
    },
    [load]
  );

  const onDismiss = useCallback(
    async (row: HeldRow): Promise<void> => {
      setBusyId(row.id);
      // C4 (Decision I) — dismiss stays a direct .update (no product created -> no SD-gated work); it now
      // also writes a labeled-reject decision_snapshot (a negative example for the match-tuning corpus).
      const snapshot: DecisionSnapshot = {
        action: 'dismiss',
        master_chosen: null,
        suggestions: [],
        picked_from_suggestions: false,
      };
      const { error: e } = await supabase
        .from('intake_resolution_queue')
        .update({ status: 'dismissed', decision_snapshot: snapshot as unknown as Json })
        .eq('id', row.id);
      setToast(
        e
          ? { kind: 'error', message: `Dismiss failed: ${e.message}` }
          : { kind: 'success', message: 'Dismissed — no product created.' }
      );
      setDismissFor(null);
      setBusyId(null);
      await load();
    },
    [load]
  );

  return (
    <div className="-m-4 flex h-full flex-col">
      <div className="flex-shrink-0 border-b border-border bg-surface-1 px-4 py-2">
        <div className="flex items-center justify-between">
          <h1 className="flex items-center gap-2 text-base font-semibold text-ink-bright">
            Intake Resolution
            <span className="text-2xs font-normal text-ink-muted">{rows.length} held</span>
          </h1>
          <button
            onClick={() => void load()}
            disabled={loading}
            className="arbi-btn-ghost flex items-center gap-1.5 px-2 py-1 text-xs disabled:opacity-50"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
        <p className="mt-0.5 text-2xs text-ink-muted">
          Clearance (supplier-sheet) rows needing a human link to a master — no clean UPC, or a UPC
          matching multiple masters. Open Link to see advisory same-brand suggestions.
        </p>
      </div>

      <div className="flex-1 overflow-auto px-4 py-3">
        {loading ? (
          <p className="py-8 text-center text-sm text-ink-muted">Loading…</p>
        ) : error ? (
          <p className="py-8 text-center text-sm text-danger">{error}</p>
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-ink-muted">
            No clearance rows awaiting resolution.
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-2xs uppercase tracking-wider text-ink-muted">
                <th className="w-6 px-2 py-1.5" />
                <th className="px-2 py-1.5 text-left">UPC</th>
                <th className="px-2 py-1.5 text-left">Part # / Model</th>
                <th className="px-2 py-1.5 text-left">Brand / Title</th>
                <th className="px-2 py-1.5 text-right">Cost</th>
                <th className="px-2 py-1.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {rows.map((r) => (
                <Fragment key={r.id}>
                  <tr className="hover:bg-surface-2">
                    <td className="px-2 py-1.5">
                      <button
                        onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                        aria-label="Toggle raw row"
                        className="text-ink-muted hover:text-ink-primary"
                      >
                        {expanded === r.id ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                      </button>
                    </td>
                    <td className="px-2 py-1.5 font-mono text-2xs text-ink-secondary">
                      {r.upc ?? '—'}
                    </td>
                    <td className="px-2 py-1.5 text-ink-secondary">
                      {r.supplier_part_no ?? r.model ?? '—'}
                    </td>
                    <td className="max-w-md px-2 py-1.5">
                      <span className="text-ink-secondary">{r.brand ?? ''}</span>{' '}
                      <span className="text-ink-muted">{r.title ?? ''}</span>
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-ink-secondary">
                      {r.cost_usd != null ? `$${r.cost_usd.toFixed(2)}` : '—'}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      {canWrite ? (
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => setPickerFor(r)}
                            disabled={busyId === r.id}
                            className="arbi-btn-ghost flex items-center gap-1 px-2 py-0.5 text-2xs disabled:opacity-50"
                          >
                            <Link2 size={11} /> Link
                          </button>
                          <button
                            onClick={() => void onConfirmNew(r)}
                            disabled={busyId === r.id}
                            className="arbi-btn-ghost flex items-center gap-1 px-2 py-0.5 text-2xs disabled:opacity-50"
                          >
                            <PlusCircle size={11} /> New
                          </button>
                          <button
                            onClick={() => setDismissFor(r)}
                            disabled={busyId === r.id}
                            className="arbi-btn-ghost flex items-center gap-1 px-2 py-0.5 text-2xs text-ink-muted disabled:opacity-50"
                          >
                            <XCircle size={11} /> Dismiss
                          </button>
                        </div>
                      ) : (
                        <span className="text-2xs text-ink-muted">read-only</span>
                      )}
                    </td>
                  </tr>
                  {expanded === r.id && (
                    <tr className="bg-surface-2">
                      <td />
                      <td colSpan={5} className="px-2 py-2">
                        <pre className="max-h-48 overflow-auto rounded bg-surface-3 p-2 font-mono text-2xs text-ink-muted">
                          {JSON.stringify(r.raw_row ?? {}, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <MasterPickerModal
        open={pickerFor !== null}
        heldRow={pickerFor}
        onCancel={() => setPickerFor(null)}
        onPick={(masterId, snapshot) => {
          if (pickerFor) void onResolve(pickerFor, masterId, snapshot);
        }}
      />
      <ConfirmDialog
        open={dismissFor !== null}
        title="Dismiss clearance row"
        message="Dismiss this row? No product is created. You can re-import the sheet later."
        confirmLabel="Dismiss"
        onConfirm={() => {
          if (dismissFor) void onDismiss(dismissFor);
        }}
        onCancel={() => setDismissFor(null)}
      />
      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
